import { AvatarV2, SelectV2, SelectV2Option } from '@cimplico/components';
import { Ref, useEffect, useMemo, useState } from 'react';
import { useUsers } from '../../queries/use-users';
import { User, UserOverview } from '../../types/user';

type UserSelectProps = {
    title?: string;
    isOptional?: boolean;
    isMulti?: boolean;
    isDisabled?: boolean;
    placeholder?: string;
    form?: string;
    errorMessage?: string;
    disabledHint?: string;
    toIncludeIds?: string[];
    defaultValue?: UserOverview[];
    disabledUserIds?: string[];
    onChange: (users: User[]) => void;
    innerRef?: Ref<HTMLInputElement>;
};

function UserSelect({
    title,
    isMulti = false,
    isOptional = false,
    isDisabled,
    placeholder,
    form,
    errorMessage,
    disabledHint,
    toIncludeIds,
    defaultValue,
    disabledUserIds = [],
    onChange,
    innerRef,
}: UserSelectProps): JSX.Element {
    const [selected, setSelected] = useState<SelectV2Option[]>([]);
    const [allUsers, setAllUsers] = useState<User[]>([]);
    const [userSearch, setUserSearch] = useState('');
    const userQuery = useUsers({
        page: 1,
        search: `${userSearch}&searchFields=name&searchFields=email`,
        sort: '',
        filter: isDisabled ? '' : 'role=MANAGER&role=USER&role=ADMIN',
        searchFilters: isDisabled ? '' : 'searchFilters=role',
    });

    function handleUserSelect(options: SelectV2Option[]): void {
        setSelected(options);
        onChange(
            allUsers.filter((u) => {
                return options.find((value) => value.value === u.id);
            }) ?? []
        );
    }

    useEffect(() => {
        if (userQuery.data?.users && !userSearch) {
            setAllUsers(userQuery.data?.users);
        }
    }, [userQuery.data?.users]);

    useEffect(() => {
        if (selected.length === 0 && defaultValue && defaultValue.length !== 0) {
            const defaultSelected: SelectV2Option[] = defaultValue.map((val) => {
                return {
                    label: `${val.firstName} ${val.lastName}`,
                    value: val.id,
                    decoration: <AvatarV2 alt={val.firstName[0] + val.lastName[0]} size="mini" color={val.profileHexColour} />,
                };
            });
            setSelected(defaultSelected);
        }
    }, [defaultValue]);

    const allOptions = useMemo(() => {
        let options = (userQuery?.data?.users || []).map((user) => {
            return {
                label: `${user.firstName} ${user.lastName}`,
                value: user.id,
                isDisabled: disabledUserIds.includes(user.id),
                decoration: <AvatarV2 alt={user.firstName[0] + user.lastName[0]} size="mini" color={user.profileHexColour} />,
            };
        });

        if (toIncludeIds) {
            options = options.filter((opt) => toIncludeIds.includes(opt.value));
        }

        return options;
    }, [toIncludeIds, disabledUserIds, userQuery?.data?.users]);

    return (
        <div>
            <SelectV2
                size="standard"
                placeholder={placeholder || 'Select users...'}
                title={title}
                form={form}
                isOptional={isOptional}
                isMulti={isMulti}
                isLoading={userQuery.isFetching || userQuery.isLoading}
                options={allOptions}
                disabled={isDisabled}
                disabledHint={disabledHint}
                errorMessage={errorMessage}
                value={selected}
                onSearch={setUserSearch}
                onChange={handleUserSelect}
                ref={innerRef}
            />
        </div>
    );
}
export default UserSelect;
